import React from 'react';
import { sectionScoreOrNull, type ComplianceStatus } from '@privisy/api-types';
import { ScanResult } from '../../utils/types';
import ScoreRingComponent from '../ScoreRing';
import { T, gradeInkFor, isUngraded, SECTION_META, STATUS_LABELS } from '../../designTokens';
import { formatDate } from '../../utils/date';
import { buildVerdict, countGapWarnings, detectScoreCap } from './verdict';
import { isGpcOnlyScan, buildGpcVerdict, isGpcSectionInconclusive, GpcVerdictKind } from './gpc-verdict';
import ScoreExplainer from './ScoreExplainer';
import { CCPA_SECTIONS, collectCcpaChecks } from './crosscheck';

// The header sits on the light page ground (#1937). These are the
// text-safe semantic tones: each clears 4.5:1 on gray50 and white.
const HERO_SEMANTIC = {
  error: T.error,
  warning: T.warningText,
  success: T.emerald700,
} as const;

// #1661: a section chip reads a status word plus its scored-check tally, not
// a percentage. A failed critical caps a section at 49, which read as "about
// half passed"; the numeric score stays explained by "How is this scored?".
const SECTION_CHIP_STATUS: Partial<Record<ComplianceStatus, { word: string; color: string }>> = {
  compliant: { word: 'Pass', color: HERO_SEMANTIC.success },
  warning: { word: 'Review', color: HERO_SEMANTIC.warning },
  non_compliant: { word: 'Fail', color: HERO_SEMANTIC.error },
};

function StatChip({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="stat-chip">
      <span className="stat-chip__value" style={{ color: color ?? T.gray900 }}>
        {value}
      </span>
      <span className="stat-chip__label">{label}</span>
    </div>
  );
}

interface ReportHeroProps {
  result: ScanResult;
}

export default function ReportHero({ result }: ReportHeroProps) {
  const hostname = new URL(result.url).hostname.replace(/^www\./, '');
  // Set by the backend only for a redirect onto another registrable domain
  // (#1648): the findings below describe that page, not the start URL's.
  // Persisted JSON, also served through share links: a malformed value must
  // not blank the hero.
  const landedHostname = (() => {
    try {
      return result.homepageFinalUrl ? new URL(result.homepageFinalUrl).hostname.replace(/^www\./, '') : null;
    } catch {
      return null;
    }
  })();

  // Shared with the AI Crosscheck banner (#1658) so their counts can't drift.
  const allChecks = React.useMemo(() => collectCcpaChecks(result), [result]);

  const totalIssues = allChecks.filter((c) => c.status === 'non_compliant' || c.status === 'error').length;
  const totalWarnings = allChecks.filter((c) => c.status === 'warning').length;
  const totalPassing = allChecks.filter((c) => c.status === 'compliant').length;
  const duration = result.completedAt
    ? Math.round((new Date(result.completedAt).getTime() - new Date(result.startedAt).getTime()) / 1000)
    : 0;

  // Mirror the backend's getActiveChecks(): skipped/error sections don't score.
  const activeSectionChecks = CCPA_SECTIONS
    .map((k) => result.sections?.[k])
    .filter((s) => s && s.status !== 'skipped' && s.status !== 'error')
    .flatMap((s) => s!.checks || []);

  const criticalFailures = activeSectionChecks.filter(
    (c) => c.status === 'non_compliant' && c.severity === 'critical',
  ).length;
  const failingSections = CCPA_SECTIONS
    .filter((k) => {
      const sec = result.sections?.[k];
      if (!sec || sec.status === 'skipped' || sec.status === 'error') return false;
      const isRelevantFail = (c: (typeof sec.checks)[number]) =>
        criticalFailures > 0
          ? c.status === 'non_compliant' && c.severity === 'critical'
          : c.status === 'non_compliant' || c.status === 'error' || c.status === 'warning';
      return (sec.checks || []).some(isRelevantFail);
    })
    .map((k) => SECTION_META[k].label);

  // #1132 (and #1174 review): totalWarnings (below, also used for the
  // "Warnings" stat chip) is a raw count of every warning-status check —
  // still what the >=90 band's "N minor issues to address" wording needs.
  // The "N compliance gaps" wording at lower scores instead needs only the
  // ones that represent an actual gap: `advisory`-flagged warnings like
  // ".well-known GPC Declaration" are catalog-documented as non-findings
  // and would otherwise inflate the gap count on almost every scan.
  //
  // A scan that assessed nothing (a page that never loaded) persists score 0,
  // which measured nothing: no F ring, and no "needs remediation" verdict.
  const ungraded = isUngraded(result.overallStatus);
  const verdict = ungraded
    ? `${hostname} could not be scored: no compliance checks completed.`
    : buildVerdict(
        result.score,
        {
          error: totalIssues,
          warning: totalWarnings,
          gapWarning: countGapWarnings(allChecks),
          criticalFailures,
          failingSections,
        },
        hostname,
      );
  const scoreCap = detectScoreCap(result.score, activeSectionChecks, result.scoreBreakdown);

  // GPC-only scans (anonymous GPC checker) get a verdict badge instead of the
  // aggregate score — a one-section pass would otherwise read "100% compliant".
  const gpcVerdict = isGpcOnlyScan(result) ? buildGpcVerdict(result, hostname) : null;

  const GPC_VERDICT_TONE: Record<GpcVerdictKind, string> = {
    honors: HERO_SEMANTIC.success,
    substantially: HERO_SEMANTIC.warning,
    ignores: HERO_SEMANTIC.error,
    inconclusive: HERO_SEMANTIC.warning,
  };
  const GPC_VERDICT_GLYPH: Record<GpcVerdictKind, string> = { honors: '✓', substantially: '≈', ignores: '✕', inconclusive: '?' };

  return (
    <header className="report-header" data-testid="report-header">
      <p className="report-header__eyebrow">Privisy audit · {formatDate(result.startedAt)}</p>
      <div className="scan-hero__content-grid">
        <div className="scan-hero__meta">
          <h1 className="report-header__verdict">{gpcVerdict ? gpcVerdict.headline : verdict}</h1>
          <p className="report-header__domain">{hostname}</p>

          {landedHostname && (
            <p className="report-hero__redirect-note">
              {hostname} redirected to {landedHostname}. This report describes that page.
            </p>
          )}

          {gpcVerdict && <p className="report-hero__verdict">{gpcVerdict.detail}</p>}

          {!gpcVerdict && (
            <div className="scan-hero__status-label" style={{ color: ungraded ? T.gray600 : gradeInkFor(result.score) }}>
              {STATUS_LABELS[result.overallStatus]}
            </div>
          )}

          {!gpcVerdict && <ScoreExplainer cap={scoreCap} />}
          {gpcVerdict && (
            <p className="report-hero__gpc-note" style={{ margin: '8px 0 0', fontSize: 13, color: T.gray600 }}>
              This scan checked GPC signal handling only. The full CCPA scan also audits
              required disclosures, third-party trackers, and your privacy policy.
            </p>
          )}
        </div>

        <div className="report-header__ring">
          {gpcVerdict ? (
            <div
              className="gpc-verdict-badge"
              role="img"
              aria-label={gpcVerdict.headline}
              style={{
                width: 160,
                height: 160,
                borderRadius: '50%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                border: `4px solid ${GPC_VERDICT_TONE[gpcVerdict.kind]}`,
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 48, fontWeight: 700, color: GPC_VERDICT_TONE[gpcVerdict.kind] }}>
                {GPC_VERDICT_GLYPH[gpcVerdict.kind]}
              </span>
            </div>
          ) : (
            <ScoreRingComponent score={ungraded ? null : result.score} size={160} animated={true} />
          )}
        </div>
      </div>

      <div className="scan-hero__stats-row">
        <StatChip label="Issues" value={totalIssues} color={totalIssues > 0 ? HERO_SEMANTIC.error : HERO_SEMANTIC.success} />
        <StatChip label="Warnings" value={totalWarnings} color={totalWarnings > 0 ? HERO_SEMANTIC.warning : HERO_SEMANTIC.success} />
        <StatChip label="Passing" value={totalPassing} color={HERO_SEMANTIC.success} />
        {/* Pluralised off the value (#1661); PagesAssessed leads with the same count. */}
        <StatChip label={result.pagesScanned === 1 ? 'page scanned' : 'pages scanned'} value={result.pagesScanned} />
        <StatChip label="Duration" value={`${duration}s`} />
      </div>

      <div className="scan-hero__sections-sidebar">
        {CCPA_SECTIONS.filter((k) => result.sections?.[k]).map((k) => {
          const sec = result.sections![k];
          const isSkipped = !sec || sec.status === 'skipped';
          const isError = sec?.status === 'error';
          // A gpcValidation section can carry a normal terminal status (not
          // 'error') while its verdict is still inconclusive (no marketing
          // trackers to test) — every check goes non-binary, so pct reads
          // 0/red unless we check the verdict, not just status. Either
          // reading is dishonest for a run that measured nothing; the '?'
          // override below is what actually saves the display.
          const isInconclusive =
            k === 'gpcValidation' && !isSkipped && !isError && isGpcSectionInconclusive(result);
          // Routed through the shared guard (#1168) so this reads the same
          // "is there a number" answer report.ts's buildSalesReportSummary
          // does, rather than a second hand-rolled derivation of it. The
          // three-way N/A/"stage failed"/"?" label below stays this
          // component's own call — sectionScoreOrNull only says whether the
          // section is a real measurement, not what to show when it isn't.
          // The chip shows the tally, never the (possibly capped) percentage.
          const measured = sec ? sectionScoreOrNull(sec, isInconclusive) !== null : false;
          // #1896: a section 'warning' also means a HIGH/MEDIUM/LOW check
          // failed (determineSectionStatus), and such a section reads Fail,
          // matching the "Fix these" list below, never Review.
          const chipStatus: ComplianceStatus | undefined =
            sec?.status === 'warning' && sec.checks.some((c) => c.status === 'non_compliant') ? 'non_compliant' : sec?.status;
          const tone = measured && chipStatus ? SECTION_CHIP_STATUS[chipStatus] : undefined;
          const passed = sec?.score?.passedChecks ?? 0;
          const scored = sec?.score?.totalChecks ?? 0;
          const word = tone?.word ?? '';
          // A section of only non-binary checks has no tally: the word alone, never 0/0.
          const text = isSkipped
            ? 'N/A'
            : isError
              ? 'stage failed'
              : isInconclusive
                ? '?'
                : scored > 0
                  ? [word, `${passed}/${scored}`].filter(Boolean).join(' · ')
                  : word;
          const ariaLabel =
            measured && word && scored > 0 ? `${word}, ${passed} of ${scored} scored checks passing` : undefined;
          const meta = SECTION_META[k];

          return (
            <div key={k} className={`hero-stage-chip ${isSkipped ? 'hero-stage-chip--muted' : ''}`}>
              <span className="hero-stage-chip__label">{meta.label}</span>
              <span
                className="hero-stage-chip__value"
                aria-label={ariaLabel}
                style={{
                  color: isSkipped || isError
                    ? T.gray600
                    : isInconclusive
                      ? HERO_SEMANTIC.warning
                      : (tone?.color ?? T.gray600),
                }}
              >
                {text}
              </span>
            </div>
          );
        })}
      </div>
    </header>
  );
}
